import { useEffect, useState } from 'react'

import { fetchOps } from '@/lib/ops'

/**
 * Same as the backend's `endpoint_key`: product + METHOD + path with `{}` for every
 * parameter. One operationId can name several endpoints (`getIssue` in Jira Platform and
 * Software, JSM's `getProperty` for organizations and request types), an endpoint key cannot.
 */
export const endpointKey = (product: string, method: string, path: string) =>
  `${product} ${method.toUpperCase()} ${path.replace(/\{[^}]*\}/g, '{}')}`

/**
 * Returns a reverse-index: catalog endpoint → liveOperationId.
 * Keys are the `key`s of the backend's `covers_resolved`; an op without
 * `covers_resolved` falls back to its bare `covers` ids.
 * Used by Operations → All Atlassian API to show which endpoints a live operation
 * already wraps ("Covered by").
 */
export function useCoveredBy(): Map<string, string> {
  const [coveredBy, setCoveredBy] = useState<Map<string, string>>(new Map())

  useEffect(() => {
    fetchOps()
      .then((ops) => {
        const m = new Map<string, string>()
        const add = (k: string, id: string) => {
          if (!m.has(k)) m.set(k, id)
        }
        for (const op of ops) {
          if (op.covers_resolved) for (const c of op.covers_resolved) add(c.key, op.operation_id)
          else for (const c of op.covers ?? []) add(c, op.operation_id)
        }
        setCoveredBy(m)
      })
      .catch(() => {})
  }, [])

  return coveredBy
}

/** The live operation covering one catalog row, if any. */
export function liveOperationFor(
  coveredBy: Map<string, string>,
  product: string,
  op: { operationId: string; method: string; path: string },
) {
  return coveredBy.get(endpointKey(product, op.method, op.path)) ?? coveredBy.get(op.operationId)
}
